import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Tabs,
  Button,
  Tag,
  Space,
  Form,
  Input,
  DatePicker,
  Checkbox,
  Modal,
  Typography,
  Divider,
  Alert,
  Spin,
  Badge,
  Popconfirm,
  Row,
  Col,
  App,
} from 'antd';
import {
  FileProtectOutlined,
  PlusOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
  SendOutlined,
  SaveOutlined,
  WarningOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { legalDocumentsService } from '../../../shared/api/services/legal-documents.service';
import type {
  LegalDocumentListItem,
  LegalDocumentDetail,
  DocumentSection,
} from '../../../shared/api/services/legal-documents.service';

const { Text, Paragraph } = Typography;

export const LegalSettings: React.FC = () => {
  const { message } = App.useApp();
  const [activeType, setActiveType] = useState<'terms' | 'privacy'>('terms');
  const [loading, setLoading] = useState<boolean>(false);
  const [documents, setDocuments] = useState<LegalDocumentListItem[]>([]);
  const [activeDocDetail, setActiveDocDetail] = useState<LegalDocumentDetail | null>(null);
  
  // Draft Editor State
  const [isEditingDraft, setIsEditingDraft] = useState<boolean>(false);
  const [draftId, setDraftId] = useState<number | undefined>(undefined);
  const [draftRevision, setDraftRevision] = useState<number>(1);
  const [form] = Form.useForm();
  const [sections, setSections] = useState<DocumentSection[]>([]);
  const [changeSummaryList, setChangeSummaryList] = useState<string[]>([]);
  const [newChangeBullet, setNewChangeBullet] = useState<string>('');

  // Publish Modal State
  const [publishModalVisible, setPublishModalVisible] = useState<boolean>(false);
  const [publishing, setPublishing] = useState<boolean>(false);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await legalDocumentsService.getDocuments(activeType);
      if (res.success) {
        setDocuments(res.data);
        
        // Find current active document
        const active = res.data.find(d => d.effectiveState === 'active' || d.status === 'published');
        if (active) {
          const detailRes = await legalDocumentsService.getDocument(active.id);
          if (detailRes.success) {
            setActiveDocDetail(detailRes.data);
          }
        } else {
          setActiveDocDetail(null);
        }

        // Check if there is an existing draft
        const draft = res.data.find(d => d.status === 'draft');
        if (draft) {
          const draftDetailRes = await legalDocumentsService.getDocument(draft.id);
          if (draftDetailRes.success) {
            loadDraftIntoForm(draftDetailRes.data);
          }
        } else {
          setIsEditingDraft(false);
        }
      }
    } catch (err: any) {
      message.error(err.response?.data?.error?.message || 'Failed to load legal documents');
    } finally {
      setLoading(false);
    }
  }, [activeType]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const loadDraftIntoForm = (draft: LegalDocumentDetail) => {
    setDraftId(draft.id);
    setDraftRevision(draft.revision);
    setSections(draft.sections || []);
    setChangeSummaryList(draft.changeSummary || []);
    form.setFieldsValue({
      version: draft.version,
      title: draft.title,
      effectiveDate: draft.effectiveDate ? dayjs(draft.effectiveDate) : dayjs(),
      noticeText: draft.noticeText,
      requiresReacceptance: draft.requiresReacceptance,
    });
    setIsEditingDraft(true);
  };

  const handleStartNewDraft = () => {
    if (!activeDocDetail) {
      setSections([
        {
          id: 'section_1',
          heading: '1. General Terms',
          paragraphs: ['Add section content here...'],
          bullets: [],
        },
      ]);
      setChangeSummaryList(['Initial document release']);
      form.setFieldsValue({
        version: '1.0',
        title: activeType === 'terms' ? 'Merchant Terms of Service' : 'Merchant Privacy Policy',
        effectiveDate: dayjs(),
        noticeText: '',
        requiresReacceptance: false,
      });
    } else {
      // Suggest next minor version (e.g. 1.0 -> 1.1)
      const currentVer = activeDocDetail.version;
      const parts = currentVer.split('.');
      let nextVer = '1.1';
      if (parts.length >= 2) {
        const minor = parseInt(parts[1], 10);
        nextVer = `${parts[0]}.${isNaN(minor) ? 1 : minor + 1}`;
      }

      setSections(JSON.parse(JSON.stringify(activeDocDetail.sections || [])));
      setChangeSummaryList([]);
      form.setFieldsValue({
        version: nextVer,
        title: activeDocDetail.title,
        effectiveDate: dayjs(),
        noticeText: activeDocDetail.noticeText || '',
        requiresReacceptance: false,
      });
    }

    setDraftId(undefined);
    setDraftRevision(1);
    setIsEditingDraft(true);
  };

  const handleSaveDraft = async () => {
    try {
      const values = await form.validateFields();
      if (sections.length === 0) {
        message.error('A legal document must have at least one section.');
        return;
      }

      setLoading(true);
      const payload = {
        id: draftId,
        documentType: activeType,
        version: values.version,
        title: values.title,
        effectiveDate: values.effectiveDate.format('YYYY-MM-DD'),
        noticeText: values.noticeText,
        requiresReacceptance: values.requiresReacceptance,
        changeSummary: changeSummaryList,
        sections,
        revision: draftRevision,
      };

      const res = await legalDocumentsService.saveDraft(payload);
      if (res.success) {
        message.success(res.message || 'Draft saved successfully!');
        setDraftId(res.data.id);
        setDraftRevision(res.data.revision);
        fetchDocuments();
      }
    } catch (err: any) {
      if (err.response?.status === 409) {
        message.error('Conflict: This document was modified by another administrator. Reloading...');
        fetchDocuments();
      } else {
        message.error(err.response?.data?.error?.message || 'Failed to save draft');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePublishConfirm = async () => {
    setPublishing(true);
    try {
      const values = await form.validateFields();
      if (sections.length === 0) {
        message.error('A legal document must have at least one section.');
        setPublishing(false);
        return;
      }

      // 1. Ensure latest changes are saved first
      const payload = {
        id: draftId,
        documentType: activeType,
        version: values.version,
        title: values.title,
        effectiveDate: values.effectiveDate.format('YYYY-MM-DD'),
        noticeText: values.noticeText,
        requiresReacceptance: values.requiresReacceptance,
        changeSummary: changeSummaryList,
        sections,
        revision: draftRevision,
      };

      const saveRes = await legalDocumentsService.saveDraft(payload);
      const targetId = saveRes.data.id;
      const targetRevision = saveRes.data.revision;

      // 2. Publish the saved draft
      const publishRes = await legalDocumentsService.publish(targetId, targetRevision);
      if (publishRes.success) {
        message.success('Legal document published successfully!');
        setPublishModalVisible(false);
        setIsEditingDraft(false);
        setDraftId(undefined);
        fetchDocuments();
      }
    } catch (err: any) {
      message.error(err.response?.data?.error?.message || err?.message || 'Failed to publish legal document');
    } finally {
      setPublishing(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!draftId) {
      setIsEditingDraft(false);
      return;
    }
    setLoading(true);
    try {
      const res = await legalDocumentsService.deleteDraft(draftId);
      if (res.success) {
        message.success('Draft discarded successfully');
        setIsEditingDraft(false);
        setDraftId(undefined);
        fetchDocuments();
      }
    } catch (err: any) {
      message.error(err.response?.data?.error?.message || 'Failed to discard draft');
    } finally {
      setLoading(false);
    }
  };

  const addSection = () => {
    const newId = `sec_${Date.now()}`;
    setSections(prev => [
      ...prev,
      {
        id: newId,
        heading: `${prev.length + 1}. New Clause Heading`,
        paragraphs: ['Enter clause description and obligations here.'],
        bullets: [],
      },
    ]);
  };

  const updateSectionHeading = (index: number, heading: string) => {
    setSections(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], heading };
      return copy;
    });
  };

  const updateSectionParagraphs = (index: number, text: string) => {
    const lines = text.split('\n').filter(p => p.trim().length > 0);
    setSections(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], paragraphs: lines };
      return copy;
    });
  };

  const updateSectionBullets = (index: number, text: string) => {
    const bullets = text.split('\n').filter(b => b.trim().length > 0);
    setSections(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], bullets };
      return copy;
    });
  };

  const removeSection = (index: number) => {
    setSections(prev => prev.filter((_, i) => i !== index));
  };

  const addChangeBullet = () => {
    if (newChangeBullet.trim()) {
      setChangeSummaryList(prev => [...prev, newChangeBullet.trim()]);
      setNewChangeBullet('');
    }
  };

  const removeChangeBullet = (index: number) => {
    setChangeSummaryList(prev => prev.filter((_, i) => i !== index));
  };

  const scheduledDocs = documents.filter(d => d.effectiveState === 'scheduled');

  return (
    <div style={{ padding: 12 }}>
      <Tabs
        activeKey={activeType}
        onChange={key => setActiveType(key as 'terms' | 'privacy')}
        items={[
          {
            key: 'terms',
            label: (
              <span>
                <FileProtectOutlined />
                Terms of Service
              </span>
            ),
          },
          {
            key: 'privacy',
            label: (
              <span>
                <FileTextOutlined />
                Privacy Policy
              </span>
            ),
          },
        ]}
      />

      {loading && !isEditingDraft ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Active Version Overview Card */}
          {activeDocDetail ? (
            <Card
              title={
                <Space>
                  <CheckCircleOutlined style={{ color: '#52c41a' }} />
                  <span>Current Active Document</span>
                  <Tag color="green">v{activeDocDetail.version}</Tag>
                  <Tag color="blue">Effective: {activeDocDetail.effectiveDate}</Tag>
                </Space>
              }
              extra={
                !isEditingDraft && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleStartNewDraft}>
                    Create New Version Draft
                  </Button>
                )
              }
              style={{ marginBottom: 20, borderColor: '#b7eb8f' }}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Paragraph><strong>Title:</strong> {activeDocDetail.title}</Paragraph>
                  <Paragraph>
                    <strong>Published At:</strong> {activeDocDetail.publishedAt ? new Date(activeDocDetail.publishedAt).toLocaleString() : 'N/A'}
                  </Paragraph>
                  <Paragraph>
                    <strong>Content SHA-256:</strong> <Text code copyable>{activeDocDetail.contentHash}</Text>
                  </Paragraph>
                </Col>
                <Col span={12}>
                  <Paragraph>
                    <strong>Sections:</strong> {activeDocDetail.sections?.length || 0} active clauses
                  </Paragraph>
                  <Paragraph>
                    <strong>Requires Re-Acceptance:</strong>{' '}
                    {activeDocDetail.requiresReacceptance ? (
                      <Tag color="orange">Yes (Mandatory Gate)</Tag>
                    ) : (
                      <Tag color="default">No (Silent Update)</Tag>
                    )}
                  </Paragraph>
                </Col>
              </Row>
            </Card>
          ) : (
            <Alert
              message="No Published Version Found"
              description="Click below to draft and publish the initial version for this legal document."
              type="warning"
              action={
                <Button type="primary" onClick={handleStartNewDraft}>
                  Create Initial Draft
                </Button>
              }
              style={{ marginBottom: 20 }}
            />
          )}

          {/* Scheduled Future Versions */}
          {scheduledDocs.length > 0 && (
            <Card
              title={
                <Space>
                  <ClockCircleOutlined style={{ color: '#faad14' }} />
                  <span>Scheduled Future Versions</span>
                </Space>
              }
              style={{ marginBottom: 20, borderColor: '#ffe58f' }}
            >
              {scheduledDocs.map(doc => (
                <div key={doc.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Space>
                    <Badge status="processing" />
                    <strong>v{doc.version}</strong> ({doc.title})
                    <Tag color="orange">Activates: {doc.effectiveDate}</Tag>
                    {doc.requiresReacceptance && <Tag color="volcano">Requires Re-Acceptance</Tag>}
                  </Space>
                  <Text type="secondary">Published: {new Date(doc.publishedAt || '').toLocaleDateString()}</Text>
                </div>
              ))}
            </Card>
          )}

          {/* Draft Editor Section */}
          {isEditingDraft && (
            <Card
              title={
                <Space>
                  <FileProtectOutlined style={{ color: '#1890ff' }} />
                  <span>{draftId ? 'Edit Version Draft' : 'Create New Version Draft'}</span>
                  <Tag color="blue">Revision #{draftRevision}</Tag>
                </Space>
              }
              extra={
                <Space>
                  <Popconfirm
                    title="Discard Draft?"
                    description="Any unsaved changes will be lost."
                    onConfirm={handleDeleteDraft}
                    okText="Discard"
                    cancelText="Cancel"
                  >
                    <Button danger icon={<DeleteOutlined />}>
                      Discard
                    </Button>
                  </Popconfirm>
                  <Button icon={<SaveOutlined />} onClick={handleSaveDraft}>
                    Save Draft
                  </Button>
                  <Button
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={() => {
                      form.validateFields().then(() => setPublishModalVisible(true));
                    }}
                  >
                    Publish Version
                  </Button>
                </Space>
              }
              style={{ borderColor: '#91d5ff' }}
            >
              <Form form={form} layout="vertical">
                <Row gutter={16}>
                  <Col span={6}>
                    <Form.Item
                      name="version"
                      label="Document Version"
                      rules={[
                        { required: true, message: 'Version is required' },
                        { pattern: /^\d+\.\d+(\.\d+)?$/, message: 'Must follow MAJOR.MINOR format (e.g. 1.1 or 2.0)' },
                      ]}
                    >
                      <Input placeholder="e.g. 1.1" />
                    </Form.Item>
                  </Col>
                  <Col span={6}>
                    <Form.Item
                      name="effectiveDate"
                      label="Effective Date"
                      rules={[{ required: true, message: 'Effective date is required' }]}
                    >
                      <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="title"
                      label="Document Title"
                      rules={[{ required: true, message: 'Title is required' }]}
                    >
                      <Input placeholder="e.g. Merchant Terms of Service" />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="noticeText" label="Announcement Notice / Header Banner (Optional)">
                  <Input.TextArea rows={2} placeholder="Optional callout banner shown at the top of the document." />
                </Form.Item>

                <Card size="small" style={{ backgroundColor: '#fafafa', marginBottom: 20 }}>
                  <Form.Item name="requiresReacceptance" valuePropName="checked" style={{ marginBottom: 8 }}>
                    <Checkbox>
                      <strong style={{ color: '#d46b08' }}>
                        Require Re-Acceptance from Merchants upon Next Launch
                      </strong>
                    </Checkbox>
                  </Form.Item>
                  <Text type="secondary">
                    When enabled, existing merchants will see a non-dismissible modal upon next app launch requiring them to review and accept this updated policy.
                  </Text>

                  <div style={{ marginTop: 14 }}>
                    <Text strong>Summary of Key Changes (displayed in re-acceptance modal):</Text>
                    <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                      {changeSummaryList.map((item, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Tag color="orange" closable onClose={() => removeChangeBullet(idx)}>
                            • {item}
                          </Tag>
                        </div>
                      ))}
                      <Space style={{ marginTop: 4 }}>
                        <Input
                          size="small"
                          placeholder="e.g. Updated commission rate to 3.5%"
                          value={newChangeBullet}
                          onChange={e => setNewChangeBullet(e.target.value)}
                          onPressEnter={addChangeBullet}
                          style={{ width: 340 }}
                        />
                        <Button size="small" icon={<PlusOutlined />} onClick={addChangeBullet}>
                          Add Bullet
                        </Button>
                      </Space>
                    </Space>
                  </div>
                </Card>

                <Divider orientation="left">
                  <Space>
                    <span>Document Clauses & Sections ({sections.length})</span>
                    <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addSection}>
                      Add Clause Section
                    </Button>
                  </Space>
                </Divider>

                {sections.map((section, sIdx) => (
                  <Card
                    key={section.id || sIdx}
                    size="small"
                    title={`Clause #${sIdx + 1}`}
                    extra={
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => removeSection(sIdx)}
                      >
                        Remove
                      </Button>
                    }
                    style={{ marginBottom: 14 }}
                  >
                    <Form.Item label="Clause Heading" style={{ marginBottom: 10 }}>
                      <Input
                        value={section.heading}
                        onChange={e => updateSectionHeading(sIdx, e.target.value)}
                        placeholder="e.g. 1. Delivery & OTP Verification"
                      />
                    </Form.Item>

                    <Form.Item label="Paragraphs (one paragraph per line)" style={{ marginBottom: 10 }}>
                      <Input.TextArea
                        rows={3}
                        value={(section.paragraphs || []).join('\n')}
                        onChange={e => updateSectionParagraphs(sIdx, e.target.value)}
                        placeholder="Detailed legal clauses and explanations..."
                      />
                    </Form.Item>

                    <Form.Item label="Bulleted Items (optional, one per line)" style={{ marginBottom: 0 }}>
                      <Input.TextArea
                        rows={2}
                        value={(section.bullets || []).join('\n')}
                        onChange={e => updateSectionBullets(sIdx, e.target.value)}
                        placeholder="Key bullet points..."
                      />
                    </Form.Item>
                  </Card>
                ))}
              </Form>
            </Card>
          )}

          {/* Version History Table */}
          {!isEditingDraft && documents.length > 0 && (
            <Card
              title={
                <Space>
                  <HistoryOutlined />
                  <span>Version History & Audit Ledger</span>
                </Space>
              }
              style={{ marginTop: 20 }}
            >
              {documents.map(doc => (
                <div
                  key={doc.id}
                  style={{
                    padding: '10px 0',
                    borderBottom: '1px solid #f0f0f0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Space>
                    <Tag
                      color={
                        doc.status === 'published'
                          ? doc.effectiveState === 'active'
                            ? 'green'
                            : 'orange'
                          : doc.status === 'draft'
                          ? 'blue'
                          : 'default'
                      }
                    >
                      v{doc.version}
                    </Tag>
                    <strong>{doc.title}</strong>
                    <Tag color="cyan">Effective: {doc.effectiveDate}</Tag>
                    <Tag>{doc.status.toUpperCase()}</Tag>
                  </Space>
                  <Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      SHA-256: {doc.contentHash.substring(0, 16)}...
                    </Text>
                  </Space>
                </div>
              ))}
            </Card>
          )}
        </>
      )}

      {/* Publish Confirmation Modal */}
      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: '#faad14' }} />
            <span>Confirm Legal Document Publication</span>
          </Space>
        }
        open={publishModalVisible}
        onOk={handlePublishConfirm}
        onCancel={() => setPublishModalVisible(false)}
        confirmLoading={publishing}
        maskClosable={!publishing}
        okText="Confirm & Publish"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <Paragraph>
          You are about to publish <strong>{form.getFieldValue('title')} (v{form.getFieldValue('version')})</strong>.
        </Paragraph>
        <Paragraph>
          <strong>Effective Date:</strong> {form.getFieldValue('effectiveDate')?.format('YYYY-MM-DD')}
        </Paragraph>
        {form.getFieldValue('requiresReacceptance') && (
          <Alert
            message="Mandatory Re-Acceptance Enabled"
            description="Existing merchants will be prompted to accept this document upon next launch."
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}
        <Paragraph type="secondary">
          Once published, this version becomes an <strong>immutable legal record</strong>. Older versions with earlier effective dates will be automatically superseded.
        </Paragraph>
      </Modal>
    </div>
  );
};
